import React, { useState, useRef, useEffect } from 'react';
import { Wand2, Sparkles, Loader, AlertCircle, CheckCircle, ArrowRight, Brain, Workflow, Send, Bot, User, X } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { A2AWorkflowDesigner } from './A2AWorkflowDesigner';

interface AIWorkflowBuilderProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ConversationMessage {
  id: string;
  type: 'user' | 'ai' | 'system';
  content: string;
  timestamp: Date;
  metadata?: any;
}

export function AIWorkflowBuilder({ isOpen, onClose }: AIWorkflowBuilderProps) {
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [generatedWorkflow, setGeneratedWorkflow] = useState<any | null>(null);
  const [showWorkflowDesigner, setShowWorkflowDesigner] = useState(false);
  const [buildingPhase, setBuildingPhase] = useState<'conversation' | 'generating' | 'complete'>('conversation');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  // Initialize conversation when modal opens
  useEffect(() => {
    if (isOpen && conversation.length === 0) {
      const newSessionId = `ai_workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      
      // Add initial AI greeting
      const initialMessage: ConversationMessage = {
        id: '1',
        type: 'ai',
        content: `Hello! I'm your AI workflow designer. I'll help you create a powerful multi-agent workflow by understanding exactly what you need.

Let's start with the basics: What problem are you trying to solve or what task would you like to automate with multiple AI agents working together?

For example, you might say:
• "I want to automate customer feedback analysis and response generation"
• "I need a system that researches topics and creates comprehensive reports"
• "I want to build a content creation pipeline from idea to published article"

What's your vision?`,
        timestamp: new Date()
      };

      setConversation([initialMessage]);
      
      // Focus input after a short delay
      setTimeout(() => inputRef.current?.focus(), 500);
    }
  }, [isOpen, conversation.length]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || isProcessing) return;

    const userMessage: ConversationMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: currentMessage.trim(),
      timestamp: new Date()
    };

    // Add user message to conversation
    setConversation(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsProcessing(true);

    try {
      // Send conversation to AI workflow builder
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-workflow-builder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            conversation: conversation.concat(userMessage),
            session_id: sessionId,
            user_id: user?.id,
            mode: 'conversational' // New mode for iterative conversation
          })
        }
      );

      if (!response.ok) {
        throw new Error(`AI request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const aiMessage: ConversationMessage = {
          id: (Date.now() + 1).toString(),
          type: 'ai',
          content: data.response || data.message,
          timestamp: new Date(),
          metadata: data.metadata
        };

        setConversation(prev => [...prev, aiMessage]);

        // Check if AI has generated a complete workflow
        if (data.workflow) {
          setGeneratedWorkflow(data.workflow);
          setBuildingPhase('complete');
          
          // Add system message about workflow completion
          const completionMessage: ConversationMessage = {
            id: (Date.now() + 2).toString(),
            type: 'system',
            content: `🎉 **Workflow Generated Successfully!**

I've created a complete multi-agent workflow based on our conversation:

**${data.workflow.name}**
${data.workflow.description}

**Workflow Structure:**
• ${data.workflow.steps?.length || 0} steps
• ${data.workflow.connections?.length || 0} connections
• ${Array.from(new Set(data.workflow.steps?.map((s: any) => s.type) || [])).join(', ')} step types

You can now review and customize this workflow in the visual designer, or continue our conversation if you'd like to refine it further.`,
            timestamp: new Date()
          };

          setConversation(prev => [...prev, completionMessage]);
        }
      } else {
        throw new Error(data.error || 'AI request failed');
      }
    } catch (error) {
      console.error('AI conversation error:', error);
      const errorMessage: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: "I apologize, but I encountered an error while processing your message. Could you please try rephrasing your request or provide more details about what you'd like to build?",
        timestamp: new Date()
      };
      setConversation(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleOpenWorkflowDesigner = () => {
    setShowWorkflowDesigner(true);
  };

  const handleCloseWorkflowDesigner = () => {
    setShowWorkflowDesigner(false);
  };

  const handleStartOver = () => {
    setConversation([]);
    setGeneratedWorkflow(null);
    setBuildingPhase('conversation');
    setCurrentMessage('');
    
    // Re-initialize with greeting
    setTimeout(() => {
      const initialMessage: ConversationMessage = {
        id: Date.now().toString(),
        type: 'ai',
        content: `Let's start fresh! What new workflow would you like to create? I'm here to help you design something amazing.`,
        timestamp: new Date()
      };
      setConversation([initialMessage]);
    }, 100);
  };

  // Quick suggestion buttons for new users
  const quickSuggestions = [
    "Create a customer feedback analysis workflow",
    "Build a content creation pipeline",
    "Design a research and reporting system",
    "Set up automated data processing",
    "Create a multi-agent customer support system"
  ];

  const handleQuickSuggestion = (suggestion: string) => {
    setCurrentMessage(suggestion);
    setTimeout(() => sendMessage(), 100);
  };

  if (!isOpen) return null;

  if (showWorkflowDesigner && generatedWorkflow) {
    return (
      <A2AWorkflowDesigner 
        isOpen={true} 
        onClose={handleCloseWorkflowDesigner} 
        initialWorkflow={generatedWorkflow}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">AI Workflow Designer</h2>
              <p className="text-sm opacity-90">Conversational workflow creation with AI guidance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {buildingPhase === 'complete' && (
              <button
                onClick={handleStartOver}
                className="px-3 py-1 bg-white/20 rounded-lg text-sm hover:bg-white/30 transition-colors"
              >
                Start Over
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Conversation Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {conversation.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${
                message.type === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message.type !== 'user' && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.type === 'ai' 
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                    : 'bg-gray-500 text-white'
                }`}>
                  {message.type === 'ai' ? (
                    <Brain className="w-4 h-4" />
                  ) : (
                    <Workflow className="w-4 h-4" />
                  )}
                </div>
              )}
              
              <div
                className={`max-w-[80%] p-4 rounded-2xl ${
                  message.type === 'user'
                    ? 'bg-blue-600 text-white'
                    : message.type === 'ai'
                    ? 'bg-indigo-50 text-gray-900 border border-indigo-200'
                    : 'bg-green-50 text-green-900 border border-green-200'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                <p className={`text-xs mt-2 ${
                  message.type === 'user' 
                    ? 'text-blue-100' 
                    : message.type === 'ai'
                    ? 'text-indigo-600'
                    : 'text-green-600'
                }`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              
              {message.type === 'user' && (
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}
          
          {isProcessing && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-2xl">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600 animate-pulse" />
                  <span className="text-sm text-indigo-700">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Suggestions (only show at the beginning) */}
        {conversation.length <= 1 && !isProcessing && (
          <div className="px-6 pb-4">
            <p className="text-xs text-gray-500 mb-3 font-medium">Quick suggestions to get started:</p>
            <div className="flex flex-wrap gap-2">
              {quickSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickSuggestion(suggestion)}
                  className="text-xs px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg transition-colors border border-indigo-200"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Workflow Actions (show when workflow is generated) */}
        {generatedWorkflow && buildingPhase === 'complete' && (
          <div className="px-6 pb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-green-900">Workflow Ready!</h3>
                  <p className="text-sm text-green-700">Your workflow has been generated and is ready to customize.</p>
                </div>
                <button
                  onClick={handleOpenWorkflowDesigner}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Workflow className="w-4 h-4" />
                  Open Designer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                conversation.length <= 1 
                  ? "Describe the workflow you want to create..."
                  : "Continue the conversation..."
              }
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={isProcessing}
            />
            <button
              onClick={sendMessage}
              disabled={!currentMessage.trim() || isProcessing}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
            >
              {isProcessing ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
          
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-500">
              Press Enter to send • The AI will ask clarifying questions to build the perfect workflow
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Brain className="w-3 h-3" />
              {conversation.filter(m => m.type !== 'system').length} messages
            </div>
          </div>
        </div>

        {/* Conversation Tips */}
        {conversation.length > 1 && conversation.length <= 3 && !generatedWorkflow && (
          <div className="px-6 pb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium mb-1">💡 Tips for better workflows:</p>
                  <ul className="space-y-1">
                    <li>• Be specific about your inputs and desired outputs</li>
                    <li>• Mention any external systems you want to integrate</li>
                    <li>• Describe your target users and their skill level</li>
                    <li>• Let me know if you need real-time processing or batch processing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}